import { DateRange } from "react-day-picker"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { DateRangePicker } from "@/components/ui/date-range-picker"
import { DropdownSelect } from "@/components/ui/dropdown-select"
import { Switch } from "@/components/ui/switch"
import { getBetterDocEnumLabel } from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import {
  BetterDocExchangeFilterValues,
  INITIAL_EXCHANGE_FILTER_VALUES,
} from "@/modules/admin/better-doc/lib/better-doc-exchange-filter"
import { findEnumValue } from "@/modules/admin/better-doc/lib/find-enum-value"
import { de_BetterDocExchangeOperationValues } from "@/types/__generated__/union-types"

type BetterDocExchangeFiltersProps = {
  filterValues: BetterDocExchangeFilterValues
  onFilterValuesChange: (filterValues: BetterDocExchangeFilterValues) => void
}

const TEST_CASE_ONLY_OPTION_VALUE = "testCasesOnly"

const REAL_EXCHANGE_ONLY_OPTION_VALUE = "realExchangesOnly"

const getTestCaseOptionValue = (isTestCase: boolean | null) => {
  if (isTestCase === null) {
    return null
  }

  return isTestCase ? TEST_CASE_ONLY_OPTION_VALUE : REAL_EXCHANGE_ONLY_OPTION_VALUE
}

const parseTestCaseOptionValue = (optionValue: string | null | undefined) => {
  if (optionValue === TEST_CASE_ONLY_OPTION_VALUE) {
    return true
  }

  return optionValue === REAL_EXCHANGE_ONLY_OPTION_VALUE ? false : null
}

export const BetterDocExchangeFilters = ({ filterValues, onFilterValuesChange }: BetterDocExchangeFiltersProps) => {
  const { t } = useTranslation()

  const operationOptions = de_BetterDocExchangeOperationValues.map((operation) => ({
    value: operation,
    label: getBetterDocEnumLabel(t, "exchangeOperation", operation),
  }))
  const testCaseOptions = [
    { value: TEST_CASE_ONLY_OPTION_VALUE, label: t("admin.betterDoc.exchangeFilters.testCasesOnly") },
    { value: REAL_EXCHANGE_ONLY_OPTION_VALUE, label: t("admin.betterDoc.exchangeFilters.realExchangesOnly") },
  ]

  const handleDateRangeChange = (dateRange: DateRange | undefined) => {
    onFilterValuesChange({ ...filterValues, dateRange })
  }

  return (
    <div className="flex flex-wrap items-start gap-4" data-testid="better-doc-admin-exchange-filters">
      <DropdownSelect
        label={t("admin.betterDoc.exchangeFilters.operation")}
        options={operationOptions}
        defaultValue={filterValues.operation}
        onSelect={(value) =>
          onFilterValuesChange({
            ...filterValues,
            operation: findEnumValue(de_BetterDocExchangeOperationValues, value),
          })
        }
        isClearable
        className="w-72"
        testId="better-doc-admin-exchange-filter-operation"
      />
      <DropdownSelect
        label={t("admin.betterDoc.exchangeFilters.origin")}
        options={testCaseOptions}
        defaultValue={getTestCaseOptionValue(filterValues.isTestCase)}
        onSelect={(value) => onFilterValuesChange({ ...filterValues, isTestCase: parseTestCaseOptionValue(value) })}
        isClearable
        className="w-56"
        testId="better-doc-admin-exchange-filter-origin"
      />
      <DateRangePicker
        label={t("admin.betterDoc.exchangeFilters.dateRange")}
        dataTestId="better-doc-admin-exchange-filter-date-range"
        value={filterValues.dateRange}
        onChange={handleDateRangeChange}
        onReset={() => handleDateRangeChange(undefined)}
        className="w-72"
      />
      <div className="flex h-12 items-center">
        <Switch
          id="better-doc-admin-exchange-filter-failed-only"
          checked={filterValues.isFailedOnly}
          onCheckedChange={(isFailedOnly) => onFilterValuesChange({ ...filterValues, isFailedOnly })}
          label={t("admin.betterDoc.exchangeFilters.failedOnly")}
          data-testid="better-doc-admin-exchange-filter-failed-only"
        />
      </div>
      <div className="flex h-12 items-center">
        <Button
          type="button"
          variant="tertiary"
          onClick={() => onFilterValuesChange(INITIAL_EXCHANGE_FILTER_VALUES)}
          data-testid="better-doc-admin-exchange-filter-reset">
          {t("common.button.resetFilters")}
        </Button>
      </div>
    </div>
  )
}
